// RedactInformationDoneButton.jsx
import { Button } from '@mui/material';
import { Box } from '@mui/system';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import useWebSocket from 'react-use-websocket';

import { useToastNotificationContext } from '../../../../hooks/global/alert/useToastNotification';
import RESTAPIError from '../../../../interfaces/RESTAPIError';
import customerAuthService from '../../../../services/customerAuthService';
import {
  resetMessageId,
  resetPiiRemovalFlags,
} from '../../../../slices/piiRemovalSlice';
import { setRedactInfoState } from '../../../../slices/reportDataSlice';
import { RootState, useTypedSelector } from '../../../../utils/store';
import ConfirmDialog from '../../../common/dialog/confirmDialog';

import { ROLE_VALUES } from '~/constants/customerConstant';

interface OtherActionsDoneButtonProps {
  onCancel: () => void; // Callback function to hide the component
  buttonFunction: 'PIIRemoval' | 'RedactInformation';
  slug: string | null;
  referenceId: string;
  refetchData: () => void;
}

interface WSDeleteMessage {
  action: string;
  id: string;
}

//Extracts data from any cookie name available in the Cookie Document
const getCookie = (name: string): string | null => {
  const nameEQ = name + '=';
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    // Check if the cookie starts with the desired name
    if (cookie.indexOf(nameEQ) === 0) {
      // Get the value of the cookie
      const value = cookie.substring(nameEQ.length);
      return value;
    }
  }
  return null;
};

const OtherActionsDoneButton = ({
  onCancel,
  buttonFunction,
  slug,
  referenceId,
  refetchData,
}: OtherActionsDoneButtonProps) => {
  const userType = localStorage.getItem('user_type');
  const { redactedDescriptionPositions, initialRedactedDescriptionPositions } = useSelector(
    (state: RootState) => state.reportData,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [confirmSubmissionOpen, setConfirmSubmissionOpen] =
    useState<boolean>(false);
  const { createToastNotification } = useToastNotificationContext();
  const dispatch = useDispatch();
  const intl = useIntl();
  const handleCancelConfirmation = () => {
    setConfirmSubmissionOpen(false);
    dispatch(resetMessageId());
  };
  const region = useTypedSelector((state) => state.auth.user.region);
  const {
    piiRemovedDescriptionRanges,
    removeAllAttachmentsFlag,
    removeCountryFlag,
    removeReporterFlag,
    removedMessagesId,
    webSocketUrl,
    piiRemovedMessageRanges,
  } = useSelector((state: RootState) => state.piiRemoval);

  const { sendMessage } = useWebSocket(webSocketUrl, {
    //Will attempt to reconnect on all close events, such as server shutting down
    shouldReconnect: () => true, // closeEvent
    queryParams: {
      token: `${accessToken}`,
      region: region ? region : 'eu',
    },
  });

  const sendSuccessToast = () => {
    createToastNotification(
      intl.formatMessage({
        defaultMessage: 'Successfully updated redacted description',
      }),
      'success',
    );
  };

  useEffect(() => {
    const wsAccessDev = localStorage.getItem('ws_access');
    if (wsAccessDev) {
      try {
        setAccessToken(wsAccessDev);
      } catch (errorMessage) {
        createToastNotification(
          'Could not establish connection for messages.',
          'error',
        );
      }
    } else {
      const extractedAccessToken = getCookie('ws_access_token');
      if (extractedAccessToken !== null) {
        try {
          setAccessToken(extractedAccessToken);
        } catch (errorMessage) {
          const formattedError = intl.formatMessage({
            defaultMessage: 'Error occurred with messages.',
          });
          createToastNotification(formattedError, 'error');
        }
      }
    }
  }, [intl, createToastNotification]);

  const handleProceedButtonClick = async () => {
    // PROCESS PATCHING REDACTING DESCRIPTION IN BACK END
    if (slug && referenceId && buttonFunction == 'RedactInformation') {
      try {
        setIsLoading(true);
        if (
          redactedDescriptionPositions &&
          redactedDescriptionPositions.length === 0
        ) {
          //checks if the redacted description is updated without any position (used when wanting to clear any redactions)

          await customerAuthService.updateRedactedDescriptionRanges(
            referenceId,
            slug,
            [],
            true,
          );
          setIsLoading(false);
          setConfirmSubmissionOpen(false);
          dispatch(setRedactInfoState(false));
          sendSuccessToast();
          await new Promise((resolve) => setTimeout(resolve, 1000));
          refetchData(); // have page refresh on succesful response
        } else if (
          redactedDescriptionPositions &&
          redactedDescriptionPositions.length > 0
        ) {
          //If the redacted description contains position values update it on the Back end
          const newRedactedPositions = [...redactedDescriptionPositions];
          await customerAuthService.updateRedactedDescriptionRanges(
            referenceId,
            slug,
            newRedactedPositions.sort((a, b) => a[0] - b[0]),
            true,
          );
          setRedactInfoState(false);

          setIsLoading(false);
          setConfirmSubmissionOpen(false);
          dispatch(setRedactInfoState(false));
          sendSuccessToast();
          await new Promise((resolve) => setTimeout(resolve, 1000));
          refetchData();
        }
      } catch (error) {
        const apiError = error as RESTAPIError;
        setConfirmSubmissionOpen(false);
        createToastNotification(apiError.message, 'error');
        setIsLoading(false);
      }
    } else if (slug && referenceId && buttonFunction == 'PIIRemoval') {
      //PROCESS PATCHING PII REMOVAL

      // Admin Pii Removal Flags API call
      if (userType !== ROLE_VALUES.TENANT_ADMIN.toString()) {
        // request pii removal for tenant users
        //Process the messages to be in the correct format
        const newPiiRemovedMessageRanges =
          piiRemovedMessageRanges?.map(
            ({ messageId, contentPiiRemovedRanges }) => ({
              id: messageId,
              content_pii_removed_ranges: contentPiiRemovedRanges || [],
            }),
          ) || null;
        try {
          await customerAuthService.tenantPiiRemovalFlags(
            referenceId,
            removeReporterFlag,
            removeCountryFlag,
            removeAllAttachmentsFlag,
            piiRemovedDescriptionRanges?.length !== 0
              ? piiRemovedDescriptionRanges
              : undefined,
            newPiiRemovedMessageRanges,
          );

          setIsLoading(false);
          setConfirmSubmissionOpen(false);
          dispatch(resetPiiRemovalFlags()); //resets all the values in the piiRemovalSlice
          dispatch(resetMessageId());
          createToastNotification(
            intl.formatMessage({
              defaultMessage: 'Successfully requested PII removal from report.',
            }),
            'success',
          );

          await new Promise((resolve) => setTimeout(resolve, 1000));
          refetchData(); // have page refresh on succesful response
        } catch (error) {
          const apiError = error as RESTAPIError;
          setConfirmSubmissionOpen(false);
          createToastNotification(apiError.message, 'error');
          setIsLoading(false);
        }
      } else {
        const newPiiRemovedMessageRanges =
          piiRemovedMessageRanges?.map(
            ({ messageId, contentPiiRemovedRanges }) => ({
              id: messageId,
              content_pii_removed_ranges: contentPiiRemovedRanges || [],
            }),
          ) || null;
        try {
          await customerAuthService.adminPiiRemovalFlags(
            slug,
            referenceId,
            removeReporterFlag,
            removeCountryFlag,
            removeAllAttachmentsFlag,
            piiRemovedDescriptionRanges?.length !== 0
              ? piiRemovedDescriptionRanges
              : undefined,
            newPiiRemovedMessageRanges,
          );
          //Deletes messages found within the array of message ID's to be removed
          if (accessToken) {
            for (const messageId of removedMessagesId) {
              const deleteChat: WSDeleteMessage = {
                id: messageId,
                action: 'delete',
              };
              sendMessage(JSON.stringify(deleteChat));
            }
          }
          setIsLoading(false);
          setConfirmSubmissionOpen(false);
          dispatch(resetPiiRemovalFlags()); //resets all the values in the piiRemovalSlice
          dispatch(resetMessageId());
          createToastNotification(
            intl.formatMessage({
              defaultMessage: 'Successfully removed requested PII from report.',
            }),
            'success',
          );

          await new Promise((resolve) => setTimeout(resolve, 1000));
          refetchData(); // have page refresh on succesful response
        } catch (error) {
          const apiError = error as RESTAPIError;
          setConfirmSubmissionOpen(false);
          createToastNotification(apiError.message, 'error');
          setIsLoading(false);
        }
      }
    }
  };

  const checkDataToDisable = () => {
    if (
      buttonFunction === 'PIIRemoval' && (
        (piiRemovedMessageRanges && piiRemovedMessageRanges.length > 0) ||
        (piiRemovedDescriptionRanges && piiRemovedDescriptionRanges.length > 0) ||
        (removedMessagesId && removedMessagesId.length > 0) ||
        removeReporterFlag ||
        removeCountryFlag ||
        removeAllAttachmentsFlag
      )
    ) {
      return false; // enable button
    } else if (
      buttonFunction === 'RedactInformation' &&
      !_.isEqual(initialRedactedDescriptionPositions, redactedDescriptionPositions)
    ) {
      return false; // enable button
    }

    return true; // disable button
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between', // Align items with equal space between them
        marginTop: '28px',
        marginBottom: '28px',
        gap: '12px',
      }}
    >
      <Button
        onClick={onCancel} // Call the onCancel callback function when the "Cancel" button is clicked
        sx={{
          display: 'flex',
          height: '32px',
          maxWidth: '134px',
          padding: '8px 12px',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          gap: '12px',
          alignSelf: 'stretch',
          backgroundColor: 'transparent',
          color: 'rgba(61, 61, 61, 1)',
          textTransform: 'none',
          '&:hover': {
            backgroundColor: 'transparent',
          },
        }}
      >
        <FormattedMessage defaultMessage='Cancel' />
      </Button>
      <Button
        onClick={() => {
          setConfirmSubmissionOpen(true);
        }}
        disabled={checkDataToDisable()}
        sx={{
          display: 'flex',
          height: '32px',
          width: 'auto',
          padding: '8px 12px',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '12px',
          alignSelf: 'stretch',
          backgroundColor: checkDataToDisable() ? '' : 'black',
          textTransform: 'none',
          color: 'white',
          '&:hover': {
            backgroundColor: 'black',
          },
        }}
      >
        {userType !== ROLE_VALUES.TENANT_ADMIN.toString() ? (
          <FormattedMessage defaultMessage='Request Changes' />
        ) : (
          <FormattedMessage defaultMessage='Apply Changes' />
        )}
      </Button>

      {/* TODO: refactor to use `openConfirmationModal` instead of `ConfirmDialog` component */}
      <ConfirmDialog
        proceedButtonContent={
          buttonFunction == 'RedactInformation'
            ? intl.formatMessage({ defaultMessage: 'Got it' })
            : userType === ROLE_VALUES.TENANT_ADMIN.toString()
            ? intl.formatMessage({ defaultMessage: 'Apply Changes' })
            : intl.formatMessage({ defaultMessage: 'Request Changes' })
        }
        dialogContent={
          buttonFunction == 'RedactInformation'
            ? intl.formatMessage({
                defaultMessage:
                  'Redaction complete. Only authorized users can view now.',
              })
            : userType === ROLE_VALUES.TENANT_ADMIN.toString()
            ? intl.formatMessage({
                defaultMessage:
                  'After finalizing your modifications, please click "Apply Changes" to proceed. Note that these changes are irreversible.',
              })
            : intl.formatMessage({
                defaultMessage:
                  'Please confirm that the requested PII deletion is accurate. Once completed, the data cannot be recovered.',
              })
        }
        dialogTitle={intl.formatMessage({ defaultMessage: 'Confirmation' })}
        open={confirmSubmissionOpen}
        handleCancel={handleCancelConfirmation}
        handleConfirm={handleProceedButtonClick}
        isLoading={isLoading}
      />
    </Box>
  );
};

export default OtherActionsDoneButton;
